// ─── OBTO LANDING — IMPROVED v2 ───

import React, { useState, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import AboutUs from "./AboutUs.tsx";
import OBTOGuide from "./OBTOGuide.tsx";
import AutopilotCampaign from "./AutopilotCampaign.tsx";
import ControlRoomCampaign from "./ControlRoomCampaign.tsx";
import MarketingObservability from "./MarketingObservability.tsx";
// OBTO brand kit, dark theme. gold/goldLight = violet-text on dark, teal = the live violet dot, fill = deep violet with white text.
const C = {
  black: "#121214", dark: "#1A1A1E", charcoal: "#1E1E22", gunmetal: "#2E2E34",
  smoke: "#3A3A42", gray: "#9A98A3", silver: "#9A98A3", light: "#F5F3EF",
  offwhite: "#F5F3EF", white: "#FFFFFF", gold: "#B4AAFF", goldDark: "#7C6CF2",
  goldLight: "#B4AAFF", teal: "#7C6CF2", tealDark: "#5345C9",
  fill: "#5345C9", onFill: "#FFFFFF",
};

const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300..900;1,300..900&family=DM+Mono:wght@400;500&display=swap');

  * { margin: 0; padding: 0; box-sizing: border-box; }
  html { scroll-behavior: smooth; }
  body { background: ${C.black}; color: ${C.white}; font-family: 'Figtree', system-ui, sans-serif; overflow-x: hidden; }
  ::selection { background: ${C.fill}; color: ${C.onFill}; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: ${C.black}; }
  ::-webkit-scrollbar-thumb { background: ${C.smoke}; border-radius: 2px; }

  @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
  @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
  @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
`;

function Reveal({ children, delay = 0, style = {} }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{
      ...style, opacity: vis ? 1 : 0,
      transform: vis ? "translateY(0)" : "translateY(40px)",
      transition: `all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}s`,
    }}>{children}</div>
  );
}

function Nav({ currentPath, onNav }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const isAbout = currentPath === "/about-us";
  const links = isAbout ? [] : ["How It Works", "Pricing", "FAQ"].map(l => ({
    href: `#${l.toLowerCase().replace(/ /g, "-")}`,
    label: l
  }));

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      padding: scrolled ? "14px 48px" : "24px 48px",
      background: scrolled ? "rgba(18,18,20,0.9)" : "transparent",
      backdropFilter: scrolled ? "blur(20px)" : "none",
      borderBottom: scrolled ? "1px solid rgba(124,108,242,0.1)" : "none",
      transition: "all 0.4s ease",
      display: "flex", justifyContent: "space-between", alignItems: "center",
    }}>
      <span style={{ display: "inline-flex", cursor: "pointer" }} onClick={() => onNav("/")} aria-label="OBTO home">
        <img src="https://www.obto.co/ms/filereader.bto?n=mush6zo8f1132076_www_brand_obto-lockup-reverse.svg" alt="OBTO" width={112} height={26} style={{ display: "block", height: "26px", width: "auto" }} />
      </span>
      <div style={{ display: "flex", gap: "32px", alignItems: "center" }}>
        {links.map(l => (
          <a key={l.href} href={l.href} style={{
            color: C.silver, textDecoration: "none", fontSize: "13px", fontWeight: 400,
            letterSpacing: "1px", transition: "color 0.3s",
          }} onMouseEnter={e => e.target.style.color = C.gold}
             onMouseLeave={e => e.target.style.color = C.silver}>{l.label}</a>
        ))}
        <Link to="/about-us" style={{
          color: C.silver, textDecoration: "none", fontSize: "13px", fontWeight: 400,
          letterSpacing: "1px", transition: "color 0.3s",
        }} onMouseEnter={e => e.target.style.color = C.gold}
           onMouseLeave={e => e.target.style.color = C.silver}>
          About Us
        </Link>
        <a href="https://docs.obto.co" target="_blank" rel="noopener noreferrer" style={{
          color: C.silver, textDecoration: "none", fontSize: "13px", fontWeight: 400,
          letterSpacing: "1px", transition: "color 0.3s",
        }} onMouseEnter={e => e.target.style.color = C.gold}
           onMouseLeave={e => e.target.style.color = C.silver}>Docs</a>
        <a href="#get-started" style={{
          padding: "10px 24px", background: C.fill, color: C.onFill, fontSize: "13px",
          fontWeight: 600, letterSpacing: "1px", textDecoration: "none", transition: "all 0.3s",
        }} onMouseEnter={e => e.target.style.background = C.goldLight}
           onMouseLeave={e => e.target.style.background = C.gold}>Get Started</a>
      </div>
    </nav>
  );
}

function Hero() {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setTimeout(() => setLoaded(true), 300); }, []);
  const anim = (d) => ({
    opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(40px)",
    transition: `all 1s ease ${d}s`,
  });

  return (
    <section style={{ minHeight: "100vh", display: "flex", alignItems: "center", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, opacity: 0.04,
        backgroundImage: "linear-gradient(rgba(124,108,242,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(124,108,242,0.3) 1px, transparent 1px)",
        backgroundSize: "80px 80px",
      }} />
      <div style={{ position: "absolute", top: "-20%", right: "-10%", width: "800px", height: "800px",
        background: "none",
      }} />

      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "160px 48px 100px", position: "relative", width: "100%" }}>
        <div style={anim(0.2)}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "8px", padding: "6px 16px",
            border: `1px solid ${C.smoke}`, borderRadius: "100px", marginBottom: "32px",
          }}>
            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: C.teal, animation: "pulse 2s ease infinite" }} />
            <span style={{ fontSize: "12px", color: C.silver, letterSpacing: "1px" }}>Now available as an MCP server</span>
          </div>
        </div>

        <h1 style={{ fontFamily: "'Figtree', system-ui, sans-serif", fontSize: "72px", fontWeight: 700, lineHeight: 1.05, maxWidth: "900px", marginBottom: "28px", ...anim(0.4) }}>
          Build apps by<br /><span style={{ color: C.gold, fontStyle: "italic" }}>talking</span> to AI.
        </h1>

        <div style={{ marginBottom: "32px", ...anim(0.5) }}>
          <p style={{ fontSize: "20px", fontWeight: 400, lineHeight: 1.6, color: C.white, maxWidth: "800px", marginBottom: "16px" }}>
            <strong style={{ color: C.gold }}>OBTO is the first MCP-native platform that lets you build and deploy full-stack applications by simply describing them.</strong>
          </p>
          <p style={{ fontSize: "18px", fontWeight: 300, lineHeight: 1.7, color: C.silver, maxWidth: "700px" }}>
            Connect OBTO to Claude, GPT, or any MCP-compatible LLM. Describe what you want. Your app is live at a real URL — no code, no editor, no deploy button.
          </p>
        </div>

        <div style={{ display: "flex", gap: "16px", alignItems: "center", ...anim(0.8) }}>
          <a href="#get-started" style={{
            padding: "16px 40px", background: C.fill, color: C.onFill, fontSize: "14px",
            fontWeight: 600, letterSpacing: "1px", textDecoration: "none", transition: "all 0.3s",
          }} onMouseEnter={e => e.target.style.background = C.goldLight}
             onMouseLeave={e => e.target.style.background = C.gold}>Start Building Free</a>
          <a href="https://docs.obto.co" target="_blank" rel="noopener noreferrer" style={{
            padding: "16px 40px", border: `1px solid ${C.smoke}`, color: C.silver,
            fontSize: "14px", fontWeight: 400, letterSpacing: "1px", textDecoration: "none", transition: "all 0.3s",
          }} onMouseEnter={e => { e.target.style.borderColor = C.gold; e.target.style.color = C.gold; }}
             onMouseLeave={e => { e.target.style.borderColor = C.smoke; e.target.style.color = C.silver; }}>Read the Docs →</a>
        </div>

        <div style={{ marginTop: "80px", background: C.charcoal, border: `1px solid ${C.smoke}`, borderRadius: "8px", overflow: "hidden", maxWidth: "700px", ...anim(1) }}>
          <div style={{ padding: "12px 16px", background: C.dark, display: "flex", gap: "8px", borderBottom: `1px solid ${C.smoke}` }}>
            <span style={{ width: "12px", height: "12px", borderRadius: "50%", background: "#FF5F57" }} />
            <span style={{ width: "12px", height: "12px", borderRadius: "50%", background: "#FEBC2E" }} />
            <span style={{ width: "12px", height: "12px", borderRadius: "50%", background: "#28C840" }} />
            <span style={{ marginLeft: "auto", fontSize: "11px", color: C.gray }}>Claude</span>
          </div>
          <div style={{ padding: "24px", fontSize: "14px", lineHeight: 1.8 }}>
            <div style={{ color: C.silver, marginBottom: "12px" }}><span style={{ color: C.teal }}>You:</span> Build me a landing page for a custom clothing shop</div>
            <div style={{ color: C.silver, marginBottom: "8px" }}><span style={{ color: C.gold }}>Claude:</span> Deploying to OBTO...</div>
            <div style={{ color: C.gray, fontSize: "12px", marginBottom: "4px" }}><span style={{ color: C.teal }}>✔</span> obto_upsert_record → pltf_javascript: LandingPage</div>
            <div style={{ color: C.gray, fontSize: "12px", marginBottom: "12px" }}><span style={{ color: C.teal }}>✔</span> obto_upsert_record → pltf_page: index</div>
            <div style={{ color: C.white }}><span style={{ color: C.gold }}>Claude:</span> Your site is live at <span style={{ color: C.teal, textDecoration: "underline" }}>myshop.obto.co</span><span style={{ display: "inline-block", width: "2px", height: "16px", background: C.gold, marginLeft: "4px", verticalAlign: "middle", animation: "blink 1s step-end infinite" }} /></div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      n: "01",
      t: "Connect the MCP Server",
      d: "Add OBTO to Claude, GPT, or any MCP-compatible AI in seconds. One URL, no API keys, no setup scripts.",
      code: `{ "mcpServers": { "obto": { "url": "https://app.obto.co/ms/mcp" } } }`,
    },
    {
      n: "02",
      t: "Describe What You Want",
      d: "Tell your AI what to build — in plain English. A landing page, a dashboard, a full-stack app with an API. No special syntax.",
      code: `"Build me a product page for my SaaS with pricing and a waitlist form."`,
    },
    {
      n: "03",
      t: "It's Live. Instantly.",
      d: "Your app is deployed to a real public URL. Share it immediately. Come back and iterate with more conversation.",
      code: `✔ App live at: myproduct.obto.co`,
    },
  ];

  return (
    <section id="how-it-works" style={{ padding: "120px 48px", maxWidth: "1200px", margin: "0 auto" }}>
      <Reveal>
        <span style={{ fontSize: "12px", color: C.gold, letterSpacing: "4px", fontWeight: 500 }}>HOW IT WORKS</span>
        <h2 style={{ fontFamily: "'Figtree', system-ui, sans-serif", fontSize: "48px", fontWeight: 600, marginTop: "16px", marginBottom: "16px" }}>
          From idea to live app<br /><span style={{ color: C.gold, fontStyle: "italic" }}>in one conversation.</span>
        </h2>
        <p style={{ fontSize: "16px", color: C.silver, fontWeight: 300, marginBottom: "64px", maxWidth: "600px" }}>
          No dashboards to learn. No deploy buttons. The AI conversation you're already having becomes your development environment.
        </p>
      </Reveal>
      <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        {steps.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.15}>
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr", gap: "48px", alignItems: "center",
              padding: "48px", background: C.charcoal, border: `1px solid ${C.smoke}`, transition: "all 0.4s",
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = C.gold}
              onMouseLeave={e => e.currentTarget.style.borderColor = C.smoke}>
              <div>
                <div style={{ fontSize: "11px", color: C.gold, letterSpacing: "3px", fontWeight: 500, marginBottom: "16px" }}>{s.n}</div>
                <h3 style={{ fontFamily: "'Figtree', system-ui, sans-serif", fontSize: "28px", fontWeight: 600, marginBottom: "16px" }}>{s.t}</h3>
                <p style={{ fontSize: "15px", color: C.silver, lineHeight: 1.7, fontWeight: 300 }}>{s.d}</p>
              </div>
              <div style={{ background: C.dark, border: `1px solid ${C.smoke}`, borderRadius: "6px", padding: "20px 24px" }}>
                <pre style={{ fontSize: "13px", color: C.teal, fontFamily: "'DM Mono', 'Fira Code', monospace", whiteSpace: "pre-wrap", lineHeight: 1.6, margin: 0 }}>{s.code}</pre>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function WhyOBTO() {
  const pts = [
    { t: "Works with any LLM", d: "Not locked to one AI provider. Claude, GPT, Qwen, open-source — if it speaks MCP, it works with OBTO.", i: "🔌" },
    { t: "Full-stack, not just UI", d: "Backend APIs, server logic, database operations. Build real applications, not just pretty mockups.", i: "🏗️" },
    { t: "Glass Box, not Black Box", d: "Every artifact is inspectable, editable, and auditable. You always know exactly what was built and how.", i: "🔍" },
    { t: "Your conversation is the builder", d: "No dashboard to learn. No drag-and-drop. The AI interface you already use IS the development environment.", i: "💬" },
    { t: "Enterprise-grade infrastructure", d: "Built on Kubernetes with Rook-Ceph storage, TLS everywhere, and multi-tenant isolation baked in from day one.", i: "🛡️" },
    { t: "Self-hostable", d: "Need to keep data behind your firewall? OBTO can be deployed on your own infrastructure. Full control, always.", i: "🏠" },
  ];

  return (
    <section id="why-it-works" style={{ padding: "120px 48px", background: C.dark }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <Reveal>
          <span style={{ fontSize: "12px", color: C.gold, letterSpacing: "4px", fontWeight: 500 }}>WHY OBTO</span>
          <h2 style={{ fontFamily: "'Figtree', system-ui, sans-serif", fontSize: "48px", fontWeight: 600, marginTop: "16px", marginBottom: "64px" }}>
            They bolt AI onto existing tools.<br /><span style={{ color: C.gold }}>We built for AI from day one.</span>
          </h2>
        </Reveal>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "24px" }}>
          {pts.map((p, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div style={{ padding: "36px 32px", background: C.charcoal, borderTop: `3px solid ${C.smoke}`, transition: "border-color 0.3s", height: "100%" }}
                onMouseEnter={e => e.currentTarget.style.borderTopColor = C.gold}
                onMouseLeave={e => e.currentTarget.style.borderTopColor = C.smoke}>
                <div style={{ fontSize: "28px", marginBottom: "16px" }}>{p.i}</div>
                <h3 style={{ fontSize: "17px", fontWeight: 600, marginBottom: "12px" }}>{p.t}</h3>
                <p style={{ fontSize: "14px", color: C.silver, lineHeight: 1.7, fontWeight: 300 }}>{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const tiers = [
    {
      name: "Builder", price: "$0", per: "forever", accent: C.silver,
      feat: ["1 app", "appname.obto.co subdomain", "Public frontend only", "10 artifacts per app", "Community support"],
      cta: "Start Free", href: "https://www.obto.co/site/ob/signup", bg: "transparent", bc: C.silver, cc: C.silver,
    },
    {
      name: "Team", price: "$49", per: "/month", accent: C.gold, hl: true,
      feat: ["5 apps", "Custom domains", "Full-stack (backend + APIs)", "100 artifacts per app", "Team collaboration (3 seats)"],
      cta: "Start Building", href: "https://www.obto.co/site/ob/signup?plan=team", bg: C.fill, bc: C.fill, cc: C.onFill,
    },
    {
      name: "Business", price: "$149", per: "/month", accent: C.teal,
      feat: ["Unlimited apps", "Multiple custom domains", "Priority support", "500 artifacts per app", "Advanced analytics + logs"],
      cta: "Go Business", href: "https://www.obto.co/site/ob/signup?plan=business", bg: "transparent", bc: C.teal, cc: C.teal,
    },
    {
      name: "Enterprise", price: "Custom", per: "", accent: C.goldLight,
      feat: ["Self-hosted option", "SSO & RBAC", "Dedicated infrastructure", "SLA + support contract", "Custom integrations"],
      cta: "Contact Sales", href: "mailto:hello@obto.co?subject=Enterprise", bg: "transparent", bc: C.goldLight, cc: C.goldLight,
    },
  ];

  return (
    <section id="pricing" style={{ padding: "120px 48px", maxWidth: "1200px", margin: "0 auto" }}>
      <Reveal>
        <div style={{ textAlign: "center", marginBottom: "64px" }}>
          <span style={{ fontSize: "12px", color: C.gold, letterSpacing: "4px", fontWeight: 500 }}>PRICING</span>
          <h2 style={{ fontFamily: "'Figtree', system-ui, sans-serif", fontSize: "48px", fontWeight: 600, marginTop: "16px" }}>Free to start. Grows with you.</h2>
          <p style={{ fontSize: "16px", color: C.silver, marginTop: "16px", fontWeight: 300 }}>A freelancer charges $5,000. A no-code tool takes weeks to learn. OBTO: describe what you want, it's live.</p>
        </div>
      </Reveal>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "20px" }}>
        {tiers.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.1}>
            <div style={{ padding: "36px 28px", background: C.charcoal, border: `1px solid ${t.hl ? C.gold : C.smoke}`, position: "relative", transition: "all 0.4s", height: "100%", display: "flex", flexDirection: "column" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = t.accent}
              onMouseLeave={e => e.currentTarget.style.borderColor = t.hl ? C.gold : C.smoke}>
              {t.hl && <div style={{ position: "absolute", top: "-1px", left: 0, right: 0, height: "3px", background: C.gold }} />}
              <div style={{ fontSize: "11px", color: t.accent, letterSpacing: "3px", fontWeight: 500, marginBottom: "16px" }}>{t.name.toUpperCase()}</div>
              <div style={{ marginBottom: "24px" }}>
                <span style={{ fontFamily: "'Figtree', system-ui, sans-serif", fontSize: "40px", fontWeight: 700 }}>{t.price}</span>
                <span style={{ fontSize: "13px", color: C.gray, marginLeft: "4px" }}>{t.per}</span>
              </div>
              <div style={{ flex: 1 }}>
                {t.feat.map(f => (
                  <div key={f} style={{ display: "flex", gap: "10px", alignItems: "flex-start", marginBottom: "12px" }}>
                    <span style={{ color: t.accent, fontSize: "13px", marginTop: "2px" }}>✔</span>
                    <span style={{ fontSize: "13px", color: C.silver, fontWeight: 300, lineHeight: 1.5 }}>{f}</span>
                  </div>
                ))}
              </div>
              <a href={t.href} target="_blank" rel="noopener noreferrer" style={{ display: "block", textAlign: "center", padding: "13px 0", marginTop: "24px", background: t.bg, border: `1px solid ${t.bc}`, color: t.cc, fontSize: "12px", fontWeight: 600, letterSpacing: "1px", textDecoration: "none", transition: "all 0.3s" }}>{t.cta}</a>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function FAQ() {
  const [open, setOpen] = useState(null);
  const faqs = [
    { q: "What is MCP?", a: "MCP (Model Context Protocol) is the emerging standard for how AI models interact with external tools and services. Think of it like USB for AI — a universal connector. OBTO is built natively on MCP, meaning any compatible AI can use it to build and deploy applications." },
    { q: "Do I need to know how to code?", a: "No. That's the whole point. You describe what you want in plain language, and the AI writes and deploys the code. If you do know code, you can inspect and edit everything OBTO creates — nothing is hidden." },
    { q: "Which AI models work with OBTO?", a: "Any LLM that supports MCP. Today that includes Claude, GPT-4o, Qwen, and several open-source models via compatible clients. As MCP adoption grows, so does OBTO compatibility." },
    { q: "What can I build?", a: "Landing pages, portfolio sites, full-stack web applications with backends and APIs, dashboards, internal tools, SaaS products — anything that runs on the web. OBTO handles frontend and backend." },
    { q: "How is this different from Bolt, v0, or GitHub Spark?", a: "Those tools trap you in their editor or sandbox. OBTO works directly inside the AI conversation you already use — Claude, ChatGPT, etc. You never leave the chat. Plus OBTO is full-stack, LLM-agnostic, self-hostable, and built natively on MCP rather than bolted on as an afterthought." },
    { q: "Is my data safe?", a: "OBTO is built on Kubernetes with enterprise-grade infrastructure, TLS everywhere, and multi-tenant isolation. For businesses that need it, OBTO can be self-hosted behind your own firewall. Every artifact is auditable and transparent." },
    { q: "How do I connect OBTO to Claude?", a: "Add OBTO as an MCP server in your Claude settings with the URL: https://app.obto.co/ms/mcp. That's it — no API keys, no local install. Claude will have instant access to all OBTO deployment tools." },
  ];

  return (
    <section id="faq" style={{ padding: "120px 48px", background: C.dark }}>
      <div style={{ maxWidth: "800px", margin: "0 auto" }}>
        <Reveal>
          <div style={{ textAlign: "center", marginBottom: "64px" }}>
            <span style={{ fontSize: "12px", color: C.gold, letterSpacing: "4px", fontWeight: 500 }}>FAQ</span>
            <h2 style={{ fontFamily: "'Figtree', system-ui, sans-serif", fontSize: "48px", fontWeight: 600, marginTop: "16px" }}>Questions? Answers.</h2>
          </div>
        </Reveal>
        {faqs.map((f, i) => (
          <Reveal key={i} delay={i * 0.05}>
            <div style={{ borderBottom: `1px solid ${C.smoke}`, cursor: "pointer" }} onClick={() => setOpen(open === i ? null : i)}>
              <div style={{ padding: "24px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "16px", fontWeight: 500 }}>{f.q}</span>
                <span style={{ color: C.gold, fontSize: "20px", transition: "transform 0.3s", transform: open === i ? "rotate(45deg)" : "rotate(0)", flexShrink: 0, marginLeft: "16px" }}>+</span>
              </div>
              <div style={{ maxHeight: open === i ? "200px" : "0", overflow: "hidden", transition: "max-height 0.4s ease, padding 0.4s ease", padding: open === i ? "0 0 24px 0" : "0" }}>
                <p style={{ fontSize: "14px", color: C.silver, lineHeight: 1.7, fontWeight: 300 }}>{f.a}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section id="get-started" style={{ padding: "120px 48px", textAlign: "center" }}>
      <Reveal>
        <h2 style={{ fontFamily: "'Figtree', system-ui, sans-serif", fontSize: "52px", fontWeight: 700, maxWidth: "700px", margin: "0 auto 24px" }}>
          The future of software isn't written.<br /><span style={{ color: C.gold, fontStyle: "italic" }}>It's spoken.</span>
        </h2>
        <p style={{ fontSize: "16px", color: C.silver, fontWeight: 300, maxWidth: "500px", margin: "0 auto 16px" }}>
          Connect OBTO to your favorite AI and start building. Free forever to start.
        </p>
        <p style={{ fontSize: "13px", color: C.gray, fontFamily: "'DM Mono', monospace", margin: "0 auto 40px", maxWidth: "500px" }}>
          MCP URL: <span style={{ color: C.teal }}>https://app.obto.co/ms/mcp</span>
        </p>
        <div style={{ display: "flex", gap: "16px", justifyContent: "center", flexWrap: "wrap" }}>
          <a href="https://www.obto.co/site/ob/signup" target="_blank" rel="noopener noreferrer" style={{
            display: "inline-block", padding: "18px 48px", background: C.fill, color: C.onFill,
            fontSize: "15px", fontWeight: 600, letterSpacing: "1px", textDecoration: "none", transition: "all 0.3s",
          }} onMouseEnter={e => e.target.style.background = C.goldLight}
             onMouseLeave={e => e.target.style.background = C.gold}>Get Started Free</a>
          <a href="https://docs.obto.co" target="_blank" rel="noopener noreferrer" style={{
            display: "inline-block", padding: "18px 48px", border: `1px solid ${C.smoke}`, color: C.silver,
            fontSize: "15px", fontWeight: 400, letterSpacing: "1px", textDecoration: "none", transition: "all 0.3s",
          }} onMouseEnter={e => { e.target.style.borderColor = C.gold; e.target.style.color = C.gold; }}
             onMouseLeave={e => { e.target.style.borderColor = C.smoke; e.target.style.color = C.silver; }}>Read the Docs</a>
        </div>
      </Reveal>
    </section>
  );
}

function Footer() {
  const navLinks = [
    { label: "How It Works", href: "#how-it-works" },
    { label: "Pricing", href: "#pricing" },
    { label: "FAQ", href: "#faq" },
    { label: "Platform Guide", href: "#ai-application-development" },
    { label: "About Us", href: "/about-us" },
    { label: "Docs", href: "https://docs.obto.co", ext: true },
  ];
  const socialLinks = [
    { label: "X / Twitter", href: "https://x.com/obtoapp" },
    { label: "LinkedIn", href: "https://linkedin.com/company/obtoapp" },
    { label: "GitHub", href: "https://github.com/obtoapp" },
  ];

  return (
    <footer style={{ padding: "60px 48px 40px", borderTop: `1px solid ${C.charcoal}`, background: C.black }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "48px", marginBottom: "48px" }}>
          <div>
            <img src="https://www.obto.co/ms/filereader.bto?n=mush6zo8f1132076_www_brand_obto-lockup-reverse.svg" alt="OBTO" width={112} height={26} style={{ display: "block", height: "26px", width: "auto" }} />
            <p style={{ fontSize: "14px", color: C.gray, marginTop: "12px", lineHeight: 1.6, maxWidth: "280px", fontWeight: 300 }}>
              The first MCP-native platform for building and deploying full-stack apps through AI conversation.
            </p>
            <div style={{ marginTop: "20px" }}>
              <code style={{ fontSize: "11px", color: C.teal, background: C.charcoal, padding: "6px 12px", borderRadius: "4px" }}>
                https://app.obto.co/ms/mcp
              </code>
            </div>
          </div>
          <div>
            <div style={{ fontSize: "11px", color: C.gold, letterSpacing: "3px", fontWeight: 500, marginBottom: "20px" }}>PRODUCT</div>
            {navLinks.map(l => (
              <a key={l.label} href={l.href} target={l.ext ? "_blank" : undefined} rel={l.ext ? "noopener noreferrer" : undefined}
                style={{ display: "block", fontSize: "13px", color: C.gray, textDecoration: "none", marginBottom: "12px", transition: "color 0.3s", fontWeight: 300 }}
                onMouseEnter={e => e.target.style.color = C.gold}
                onMouseLeave={e => e.target.style.color = C.gray}>{l.label}</a>
            ))}
          </div>
          <div>
            <div style={{ fontSize: "11px", color: C.gold, letterSpacing: "3px", fontWeight: 500, marginBottom: "20px" }}>CONNECT</div>
            {socialLinks.map(l => (
              <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer"
                style={{ display: "block", fontSize: "13px", color: C.gray, textDecoration: "none", marginBottom: "12px", transition: "color 0.3s", fontWeight: 300 }}
                onMouseEnter={e => e.target.style.color = C.gold}
                onMouseLeave={e => e.target.style.color = C.gray}>{l.label}</a>
            ))}
            <a href="mailto:hello@obto.co"
              style={{ display: "block", fontSize: "13px", color: C.gray, textDecoration: "none", marginBottom: "12px", transition: "color 0.3s", fontWeight: 300 }}
              onMouseEnter={e => e.target.style.color = C.gold}
              onMouseLeave={e => e.target.style.color = C.gray}>hello@obto.co</a>
          </div>
        </div>
        <div style={{ borderTop: `1px solid ${C.charcoal}`, paddingTop: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: "12px", color: C.gray }}>© 2026 OBTO. All rights reserved.</div>
          <div style={{ fontSize: "12px", color: C.gray }}>Built with OBTO.</div>
        </div>
      </div>
    </footer>
  );
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAbout = location.pathname === "/about-us";
  const navigateTo = (path) => { navigate(path); window.scrollTo(0, 0); };

  if (["/obto-mcp-platform", "/obto-operations-control-room"].includes(location.pathname) || ["#/obto-mcp-platform", "#/obto-operations-control-room"].includes(window.location.hash)) {
    return <ControlRoomCampaign />;
  }

  if (location.pathname === "/from-prompt-to-autopilot" || window.location.hash === "#/from-prompt-to-autopilot") {
    return <AutopilotCampaign />;
  }

  return (
    <div>
      <style dangerouslySetInnerHTML={{ __html: globalCSS }} />
      <Nav currentPath={location.pathname} onNav={navigateTo} />
      <Routes>
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="/" element={
          <>
            <Hero />
            <div style={{ borderTop: `1px solid ${C.charcoal}`, borderBottom: `1px solid ${C.charcoal}`, padding: "16px 0", overflow: "hidden" }}>
              <div style={{ display: "inline-block", animation: "marquee 20s linear infinite", whiteSpace: "nowrap" }}>
                <span style={{ fontSize: "12px", color: C.gray, letterSpacing: "4px", fontWeight: 300 }}>
                  {"CLAUDE  •  GPT  •  QWEN  •  OPEN-SOURCE MODELS  •  ANY LLM WITH MCP  •  MCP-NATIVE PLATFORM  •  FULL-STACK DEPLOYMENT  •  ".repeat(2)}
                </span>
              </div>
            </div>
            <HowItWorks />
            <WhyOBTO />
            <Pricing />
            <FAQ />
            <CTA />
            <OBTOGuide />
            <Footer />
          </>
        } />
      </Routes>
    </div>
  );
}

export default function OBTOLanding() {
  return (
    <BrowserRouter>
      <MarketingObservability />
      <AppRouter />
    </BrowserRouter>
  );
}

const mount = () => {
  const element = document.getElementById("root");
  if (!element) return;
  createRoot(element).render(<OBTOLanding />);
};

document.readyState === "loading"
  ? document.addEventListener("DOMContentLoaded", mount, { once: true })
  : mount();
